import { Component, type ErrorInfo, type ReactNode } from 'react';
import { StateScreen } from '@/components/StateScreen';
import { Btn } from '@/components/ui/tiles';
import { TriangleAlert, Send, Check, RefreshCw, Copy } from 'lucide-react';
import { BotDisabledError, TelegramUnavailableError } from '@/lib/api';
import { BotDisabledScreen } from '@/components/BotDisabledScreen';
import { TelegramUnavailableScreen } from '@/components/TelegramUnavailableScreen';
import { autoLog, reportError } from '@/lib/error-report';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
}

type Phase = 'idle' | 'sending' | 'sent' | 'failed';

interface State {
  error: Error | null;
  phase: Phase;
  code: string;
  description: string;
}

// Класс-компонент — hooks недоступны, поэтому зовём `i18n.t` напрямую. Инициализация
// словаря идёт на импорте `./i18n` в main.tsx, до первого рендера: если бы она упала,
// не смонтировался бы и сам ErrorBoundary, так что тут словарь всегда готов.
// reportCode показываем ПОСЛЕ отправки — тогда он гарантированно есть в БД (юзер
// диктует его разработчику). Тихий авто-лог краша летит сразу.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, phase: 'idle', code: '', description: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    // Тихий авто-лог — даже если юзер просто перезагрузит, краш до нас дойдёт.
    void autoLog(error, { severity: 'crash', componentStack: info.componentStack ?? undefined }).catch(
      () => {},
    );
  }

  private send = async () => {
    const err = this.state.error;
    if (!err) return;
    this.setState({ phase: 'sending' });
    const res = await reportError(err, {
      kind: 'user_report',
      severity: 'crash',
      description: this.state.description.trim() || undefined,
      componentStack: undefined,
    });
    this.setState({ phase: res.ok ? 'sent' : 'failed', code: res.reportCode });
  };

  private copyCode = () => {
    navigator.clipboard?.writeText(this.state.code).catch(() => {});
  };

  render() {
    if (!this.state.error) return this.props.children;
    const err = this.state.error;
    // Telegram не отдал данные для входа — не наша поломка, отдельный экран.
    if (err instanceof TelegramUnavailableError) {
      return <TelegramUnavailableScreen reason={err.reason} />;
    }
    // Block B.9: специальный экран для 401/403.
    if (err instanceof BotDisabledError) {
      return <BotDisabledScreen />;
    }

    const { phase, code, description } = this.state;
    const done = phase === 'sent' || phase === 'failed';

    return (
      <div className="min-h-screen bg-background text-foreground">
        <StateScreen icon={<TriangleAlert />} tone="danger" title={i18n.t('Crash title')} description={err.message || i18n.t('Unknown error')}>
          {done ? (
            <div className="space-y-3 text-left">
              <p className="text-center text-[14px] text-[var(--t-muted)]">
                {phase === 'sent'
                  ? i18n.t('Thanks, developers will look into it')
                  : i18n.t('Could not send, save the code')}
              </p>
              <div className="rounded-[var(--r-card)] border border-[var(--t-line)] bg-[var(--t-surface)] px-4 py-3">
                <div className="mb-1 text-[12px] text-[var(--t-muted)]">{i18n.t('Report code')}</div>
                <div className="flex items-center justify-between gap-3">
                  <span className="select-all font-mono text-[18px] font-semibold">{code}</span>
                  <button
                    type="button"
                    onClick={this.copyCode}
                    className="flex items-center gap-1.5 rounded-[var(--r-sm)] bg-[var(--t-surface-2)] px-3 py-2 text-[13px] font-medium active:scale-95 transition-transform"
                  >
                    <Copy size={14} /> {i18n.t('Copy')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <textarea
                value={description}
                onChange={(e) => this.setState({ description: e.target.value })}
                placeholder={i18n.t('Describe what happened (optional)')}
                rows={3}
                maxLength={2000}
                className="w-full resize-none rounded-[var(--r-card)] border border-[var(--t-line)] bg-[var(--t-surface)] px-4 py-3 text-[16px] text-foreground placeholder:text-[var(--t-muted)] focus:border-[var(--t-fill-strong)] focus:outline-none"
              />
              <Btn className="w-full" icon={<Send />} onClick={this.send} disabled={phase === 'sending'}>
                {phase === 'sending' ? i18n.t('Sending…') : i18n.t('Send report to developers')}
              </Btn>
            </>
          )}
          <Btn variant="ghost" className="w-full" icon={<RefreshCw />} onClick={() => window.location.reload()}>
            {i18n.t('Reload page')}
          </Btn>
        </StateScreen>
      </div>
    );
  }
}
